import { Component, OnInit, AfterViewInit, ViewChild, ElementRef, HostListener, Input } from '@angular/core';
import { TimeDistributionService } from './time-distribution.service';
import { PieChartData, TimeDistributionData } from '../../models/time-distribution.models';

@Component({
    selector: 'app-time-distribution',
    templateUrl: './time-distribution.component.html',
    styleUrls: ['./time-distribution.component.scss'],
    standalone: false
})
export class TimeDistributionComponent implements OnInit, AfterViewInit {
  @ViewChild('chartContainer', { static: false }) chartContainer!: ElementRef;

  // Input property to control filter visibility from parent component
  @Input() isFilterVisible: boolean = false;

  // Chart options
  view: any[] = [500, 300];
  gradient: boolean = false;
  showLegend: boolean = true;
  showLabels: boolean = true;
  isDoughnut: boolean = false;
  legendPosition: string = 'bottom';
  legendTitle: string = 'Legend';
  animations: boolean = true;
  
  // Color scheme for the pie chart - using a professional color palette
  colorScheme = {
    domain: [
      '#4285F4', // Blue
      '#EA4335', // Red
      '#34A853', // Green
      '#FBBC05', // Yellow
      '#8E24AA', // Purple
      '#00ACC1', // Cyan
      '#FB8C00', // Orange
      '#607D8B', // Blue Grey
      '#1E88E5', // Light Blue
      '#43A047'  // Light Green
    ]
  };

  // Chart data
  chartData: PieChartData[] = [];
  originalData: TimeDistributionData[] = [];
  
  // Loading state
  isLoading: boolean = false;

  constructor(
    private timeDistributionService: TimeDistributionService
  ) {}

  ngOnInit(): void {
    this.fetchTimeDistributionData();
  }
  
  ngAfterViewInit(): void {
    // Initialize chart size after view is initialized
    setTimeout(() => {
      this.updateChartSize();
    }, 100);
  }


  fetchTimeDistributionData(): void {
    this.isLoading = true;
    
    this.timeDistributionService.getTimeDistributionData({}).subscribe(
      (response: any) => {
        this.isLoading = false;
        if (response && response.data) {
          this.originalData = response.data;
          this.processData(response.data);
          this.updateChartSize();
        }
      },
      (error: any) => {
        this.isLoading = false;
        console.error('Error fetching time distribution data:', error);
      }
    );
  }

  processData(data: TimeDistributionData[]): void {
    // Filter out any entries with null or undefined practiceArea or estimatedHours
    const filteredData = data.filter(item => 
      item.practiceArea && 
      item.estimatedHours !== null && 
      item.estimatedHours !== undefined
    );
    
    // If no valid data, set empty chart data
    if (filteredData.length === 0) {
      this.chartData = [];
      return;
    }
    
    // Calculate total estimated hours
    const totalHours = filteredData.reduce((sum, item) => sum + (item.estimatedHours || 0), 0);
    
    // Transform data into pie chart format with percentages and round values
    let chartItems = filteredData.map(item => {
      const hours = Math.round(item.estimatedHours || 0); // Round to whole numbers
      const percentage = totalHours > 0 ? Math.round((hours / totalHours) * 100) : 0; // Round percentages
      
      return {
        name: item.practiceArea || 'Unknown',
        value: hours,
        percentage: percentage
      };
    });
    
    // Store original items for reference (though not needed for grouping anymore)
    this.originalItems = [...chartItems];
    
    // Sort by value (descending)
    this.chartData = chartItems.sort((a, b) => b.value - a.value);
    
    console.log('Processed chart data:', this.chartData);
  }
  

  @HostListener('window:resize')
  updateChartSize(): void {
    if (this.chartContainer) {
      const containerWidth = this.chartContainer.nativeElement.offsetWidth || 500;
      
      // Pie charts work well with aspect ratio - keep compact
      const chartWidth = Math.max(containerWidth - 40, 300);
      const chartHeight = Math.min(chartWidth * 0.65, 320); // More compact for pie
      
      this.view = [chartWidth, chartHeight];
    }
  }
  
  // Store original items for reference
  private originalItems: PieChartData[] = [];

  // Custom tooltip formatting
  formatTooltip(item: any): string {
    if (!item || !item.data) return '';
    const data = item.data;
    const name = data.name || 'Unknown';
    const value = data.value || 0;
    const percentage = data.percentage ? data.percentage.toFixed(1) : '0.0';
    
    return `${name}: ${value} hours (${percentage}%)`;
  }
}
